import React from 'react';
import { getRoleIconComponent } from '../utils/roleIcons';

interface RoleLike {
  name: string;
  description?: string;
  priority: number;
  icon?: string | null;
}

type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

interface MemberAvatarProps {
  member: {
    vorname?: string;
    name?: string;
    active?: number;
    roles?: RoleLike[];
  };
  /** Feste Rolle für Kontexte ohne Rollendaten (z.B. Gäste → 'guest'). Wird nur genutzt, wenn member.roles leer ist. */
  role?: string;
  photoUrl?: string;
  hasImageError?: boolean;
  onImageError?: () => void;
  onClick?: () => void;
  size?: AvatarSize;
  className?: string;
}

// Größen-Varianten: Container, Initialen, Badge, Badge-Icon
const SIZES: Record<AvatarSize, { container: string; text: string; badge: string; icon: string }> = {
  sm: { container: 'h-9 w-9', text: 'text-xs', badge: 'w-4 h-4', icon: 'w-2.5 h-2.5' },
  md: { container: 'h-12 w-12', text: 'text-sm', badge: 'w-5 h-5', icon: 'w-3 h-3' },
  lg: { container: 'h-16 w-16', text: 'text-xl', badge: 'w-6 h-6', icon: 'w-3.5 h-3.5' },
  xl: { container: 'h-24 w-24', text: 'text-3xl', badge: 'w-7 h-7', icon: 'w-4 h-4' },
};

// Farben pro Rolle: Pastell-Hintergrund + Initialen-Farbe + Badge-Farbe
const AVATAR_COLORS: Record<string, { bg: string; text: string; badge: string }> = {
  board: {
    bg: 'bg-primary-100 dark:bg-primary-900/40',
    text: 'text-primary-700 dark:text-primary-300',
    badge: 'bg-primary-600',
  },
  trainer: {
    bg: 'bg-info-100 dark:bg-info-900/40',
    text: 'text-info-700 dark:text-info-300',
    badge: 'bg-info-600',
  },
  auditor: {
    bg: 'bg-warning-100 dark:bg-warning-900/40',
    text: 'text-warning-700 dark:text-warning-300',
    badge: 'bg-warning-500',
  },
  member: {
    bg: 'bg-success-100 dark:bg-success-900/40',
    text: 'text-success-700 dark:text-success-300',
    badge: 'bg-success-600',
  },
  guest: {
    bg: 'bg-amber-100 dark:bg-amber-900/40',
    text: 'text-amber-700 dark:text-amber-300',
    badge: 'bg-amber-500',
  },
  default: {
    bg: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-600 dark:text-gray-300',
    badge: 'bg-gray-400',
  },
};

const MemberAvatar: React.FC<MemberAvatarProps> = ({
  member,
  role,
  photoUrl,
  hasImageError,
  onImageError,
  onClick,
  size = 'md',
  className = '',
}) => {
  const primaryRole = member.roles && member.roles.length > 0
    ? [...member.roles].sort((a, b) => b.priority - a.priority)[0]
    : role
      ? { name: role, priority: 0 } as RoleLike
      : undefined;
  // Nur explizit inaktive Mitglieder rot darstellen (active === 0); Kontexte ohne active-Feld (z.B. Gäste) bleiben neutral.
  const isInactive = member.active !== undefined && member.active !== 1;

  const colors = isInactive
    ? {
        bg: 'bg-error-100 dark:bg-error-900/40',
        text: 'text-error-700 dark:text-error-300',
        badge: 'bg-error-500',
      }
    : AVATAR_COLORS[primaryRole?.name || 'default'] || AVATAR_COLORS.default;

  const dims = SIZES[size];
  const initials = `${member.vorname?.charAt(0) || ''}${member.name?.charAt(0) || ''}`.toUpperCase();
  const BadgeIcon = getRoleIconComponent(primaryRole);

  return (
    <div
      className={`relative flex-shrink-0 ${dims.container} ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''} ${className}`}
      onClick={onClick}
    >
      {photoUrl && !hasImageError ? (
        <img
          src={photoUrl}
          alt={`${member.vorname} ${member.name}`}
          className="h-full w-full rounded-full object-cover border-2 border-gray-200 dark:border-gray-600"
          onError={onImageError}
        />
      ) : (
        <div
          className={`h-full w-full rounded-full ${colors.bg} border-2 border-gray-200 dark:border-gray-600 flex items-center justify-center`}
        >
          <span className={`${dims.text} font-bold ${colors.text}`}>{initials || '?'}</span>
        </div>
      )}
      {/* Rollen-Badge unten rechts */}
      {primaryRole && (
        <span
          className={`absolute -bottom-0.5 -right-0.5 ${dims.badge} rounded-full ${colors.badge} ring-2 ring-white dark:ring-gray-800 flex items-center justify-center`}
          title={primaryRole.description || primaryRole.name}
        >
          <BadgeIcon className={`${dims.icon} text-white`} />
        </span>
      )}
    </div>
  );
};

export default MemberAvatar;
